import React from 'react';
import {
  Action,
  ActionDropDown,
  ActionIconDropdown,
  Card,
  CardSkeleton,
  HStack,
  ICON_BRIEFCASE_OUTLINE,
  ICON_CHECK,
  ICON_CUSTOM_SORT_PLUS,
  ICON_SORT,
  Message,
  Select,
  SimpleTable,
  SimpleTableSkeleton,
  Spacing,
  Stack,
} from '@doveit/bricks';
import { omit, unionBy } from 'lodash';
import SimplePageLayout from '../../../layouts/simple-page-layout/SimplePageLayout';
import useIsDevice from '../../../hooks/use-is-device/useIsDevice';
import { useSerpJobApplications } from '../../hooks/use-serp-job-applications/useSerpJobApplications';
import usePagination from '../../../hooks/use-pagination/usePagination';
import PaginatedList from '../../../components/paginated-list/PaginatedList';
import ViewSerpJobApplicationTableRow from '../../containers/view-serp-job-application-table-row/ViewSerpJobApplicationTableRow';
import useRBAC from '../../../hooks/use-rbac/useRBAC';
import useFilters from '../../../hooks/use-filters/useFilters';
import { useAgentFilter } from '../../../hooks/agent-filter/useAgentFilter';
import SearchByQueryAction from '../../../components/search-by-query-action/SearchByQueryAction';
import CreateJobApplicationWithApplicantAction from '../../containers/create-job-application-with-applicant-action/CreateJobApplicationWithApplicantAction';
import SerpFiltersSummary from '../../../components/serp-filters-summary/SerpFiltersSummary';
import { ViewSerpJobApplicationCard } from '../../containers/view-serp-job-application-card/ViewSerpJobApplicationCard';
import {
  ALL_JOB_APPLICATIONS_FILTER_OPTION,
  DEFAULT_FILTER_OPTION,
  DEFAULT_SORT_OPTION,
  FILTER_OPTIONS,
  getCombinedFiltersMapper,
  GROUPED_FILTER_OPTIONS,
  IN_MANAGEMENT_FILTER_OPTION,
  LOAD_SERP_JOB_APPLICATIONS_ERROR_MESSAGE,
  NO_SERP_JOB_APPLICATIONS_MESSAGE,
  SORT_OPTIONS,
  SORT_OPTIONS_PER_FILTER,
  TO_BE_MANAGED_FILTER_OPTION,
} from './constants';
import { CombinedJobApplicationFilters, JobApplicationSerpPageFilterKey } from './types';
import FilterSerpJobApplicationsAction from '../../containers/filter-serp-job-applications-action/FilterSerpJobApplicationsAction';
import { delay } from '../../../utils/delay/delay';
import useJobPositions from '../../../job-position/hooks/use-job-positions/useJobPositions';
import getShortAgentNameFromEmail from '../../../utils/agent/getShortAgentNameFromEmail';
import { UserFilterParam } from '../../../hooks/use-filters/params/userFilterParam';
import { EmailFilterStrategy } from '../../../providers/api/serp-job-applications/getSerpJobApplications';

const JobApplicationSerpPage: React.FC = () => {
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const isMobile = useIsDevice('mobile');

  const { page, paginationQueryParam, goToPage } = usePagination('page', scrollRef);

  const { userIsAgent, userIsHR, userIsAdmin } = useRBAC();
  const filterContext = React.useMemo(
    () => ({ userIsAgent, userIsHR, userIsAdmin }),
    [userIsAgent, userIsHR, userIsAdmin],
  );

  const combinedMapper = React.useMemo(
    () => getCombinedFiltersMapper(filterContext),
    [filterContext],
  );

  const {
    agentEmails: agents,
    isLoading: isAgentFilterLoading,
  } = useAgentFilter(paginationQueryParam);

  const {
    filters,
    addFilters,
    removeFilters,
  } = useFilters<CombinedJobApplicationFilters>(combinedMapper);

  const apiFilters = React.useMemo(() => {
    const baseFilters = FILTER_OPTIONS(filterContext)
      .find(({ key }) => key === filters.filter)?.value ?? {};

    const advancedFilters = omit(filters, ['filter', 'sort', 'assigneeEmail', 'interviewerEmail', 'supervisorEmail']);
    const agentEmails = agents.map((it) => it.email);
    const assigneeEmail = filters.assigneeEmail?.map((it) => it.email) || agentEmails;
    const interviewerEmail = filters.interviewerEmail?.map((it) => it.email) || agentEmails;
    const supervisorEmail = filters.supervisorEmail?.map((it) => it.email) || [];

    let emailFilters = {};
    if (filters.filter === 'inManagement') {
      const emails = new Set([...assigneeEmail, ...supervisorEmail]);
      emailFilters = {
        emailFilter: [...emails],
        emailFilterStrategy: [EmailFilterStrategy.ASSIGNEE, EmailFilterStrategy.SUPERVISOR],
      };
    } else {
      const emailList = [...new Set([...assigneeEmail, ...supervisorEmail, ...interviewerEmail])];
      const strategies: EmailFilterStrategy[] = [
        ...(assigneeEmail.length > 0 ? [EmailFilterStrategy.ASSIGNEE] : []),
        ...(interviewerEmail.length > 0 ? [EmailFilterStrategy.INTERVIEWER] : []),
        ...(supervisorEmail.length > 0 ? [EmailFilterStrategy.SUPERVISOR] : []),
      ];
      emailFilters = {
        emailFilter: emailList,
        ...(strategies.length > 0 ? { emailFilterStrategy: strategies } : {}),
      };
    }
    return { ...baseFilters, ...advancedFilters, ...emailFilters };
  }, [agents, filterContext, filters]);

  const sortOption = React.useMemo(() => {
    if (filters.sort && filters.sort !== DEFAULT_SORT_OPTION.key) {
      return SORT_OPTIONS.find(({ key }) => key === filters.sort) ?? DEFAULT_SORT_OPTION;
    }

    if (filters.filter) {
      return SORT_OPTIONS_PER_FILTER[filters.filter!];
    }

    return DEFAULT_SORT_OPTION;
  }, [filters.filter, filters.sort]);

  const {
    data: serpJobApplications,
    isLoading: areSerpJobApplicationsLoading,
    error: serpJobApplicationsError,
    mutate: mutateSerpJobApplications,
  } = useSerpJobApplications(isAgentFilterLoading ? undefined : {
    ...apiFilters,
  }, {
    page,
    sort: sortOption.value,
  });

  const filterLabel = React.useMemo(() => {
    if (filters.filter === ALL_JOB_APPLICATIONS_FILTER_OPTION.key) {
      return '';
    }

    const filterObject = FILTER_OPTIONS(filterContext).find((option) => option.key === filters.filter);

    return filterObject?.label.long.toLowerCase();
  }, [filterContext, filters.filter]);

  const handleBadgeClick = React.useCallback((badgeFilter: JobApplicationSerpPageFilterKey) => () => {
    addFilters({ filter: badgeFilter }, { deleteKeys: [paginationQueryParam] });
  }, [addFilters, paginationQueryParam]);

  const handleSearch = React.useCallback((query: string) => {
    if (query) {
      addFilters({ q: query }, { deleteKeys: [paginationQueryParam] });
    } else {
      removeFilters(['q', paginationQueryParam]);
    }
  }, [addFilters, paginationQueryParam, removeFilters]);

  const handleJobApplicationCreated = React.useCallback(async () => {
    await delay(0.5 * 1000);
    mutateSerpJobApplications();
  }, [mutateSerpJobApplications]);

  const mutateJobApplicationsWithDelay = React.useCallback(async () => {
    await delay(0.5 * 1000);
    mutateSerpJobApplications();
  }, [mutateSerpJobApplications]);

  const defaultFilterKey = DEFAULT_FILTER_OPTION(filterContext);
  const inManagementOption = IN_MANAGEMENT_FILTER_OPTION(filterContext);

  const { data: jobPositions = [] } = useJobPositions((apiFilters.jobPositionId || []).length > 0);

  const advancedFilterSummaryConfigs = React.useMemo(() => [
    {
      key: 'province',
      label: 'Province',
    },
    {
      key: 'jobPositionId',
      label: 'Posizione',
      formatValue: (ids: string[]) => jobPositions
        ?.filter((it) => ids.includes(it.id))
        ?.map((it) => it.name)
        ?.sort(),
    },
    {
      key: 'supervisorEmail',
      label: 'Supervisore',
      formatValue: (values: UserFilterParam[]) => values
        ?.map((it) => it.shortName || getShortAgentNameFromEmail(it.email))
        ?.sort(),
    },
    {
      key: 'assigneeInterviewer',
      label: 'Assegnatario/Esaminatore',
      formatValue: (values: UserFilterParam[]) => values
        ?.map((it) => it.shortName || getShortAgentNameFromEmail(it.email))
        ?.sort(),
    },
    {
      key: 'hasLicense',
      label: 'Patentino',
      formatValue: (value: boolean) => {
        switch (value) {
          case true:
            return 'Si';
          case false:
            return 'No';
          default:
            return undefined;
        }
      },
    },
    {
      key: 'isFranchising',
      label: 'Franchising',
      formatValue: (value: boolean) => {
        switch (value) {
          case true:
            return 'Si';
          case false:
            return 'No';
          default:
            return undefined;
        }
      },
    },
  ], [jobPositions]);

  return (
    <SimplePageLayout
      toolbar={(
        <HStack
          alignItems="center"
          justifyContent="space-between"
          gap={100}
        >
          <div style={{ flexGrow: '1' }}>
            {!isMobile && (
              <HStack aria-label="Filtri (desktop)" gap={150}>
                {(userIsHR || userIsAdmin) && (
                  <Action
                    label={TO_BE_MANAGED_FILTER_OPTION.label.short}
                    aria-label="Da gestire"
                    onClick={filters.filter !== TO_BE_MANAGED_FILTER_OPTION.key
                      ? handleBadgeClick(TO_BE_MANAGED_FILTER_OPTION.key)
                      : undefined}
                    color={filters.filter !== TO_BE_MANAGED_FILTER_OPTION.key
                      ? 'neutral'
                      : 'primary'}
                  />
                )}

                <Action
                  label={inManagementOption.label.short}
                  aria-label="In gestione"
                  onClick={filters.filter !== inManagementOption.key
                    ? handleBadgeClick(inManagementOption.key)
                    : undefined}
                  color={filters.filter !== inManagementOption.key
                    ? 'neutral'
                    : 'primary'}
                />

                {GROUPED_FILTER_OPTIONS(filterContext).map(({ label, options }) => (
                  <ActionDropDown
                    key={label}
                    label={label}
                    aria-label={`Filtra candidature per ${label}`}
                    emphasis="low"
                    color={options.some(({ key }) => key === filters.filter) ? 'primary' : 'neutral'}
                    options={options.map(({ key, label: optionLabel }) => ({
                      key,
                      label: optionLabel.short,
                      color: filters.filter !== key ? 'neutral' : 'primary',
                      iconRight: filters.filter !== key ? undefined : { path: ICON_CHECK },
                      onClick: filters.filter !== key ? handleBadgeClick(key) : undefined,
                    }))}
                  />
                ))}

                <Action
                  label={ALL_JOB_APPLICATIONS_FILTER_OPTION.label.short}
                  aria-label="Tutte le candidature"
                  onClick={filters.filter !== ALL_JOB_APPLICATIONS_FILTER_OPTION.key
                    ? handleBadgeClick(ALL_JOB_APPLICATIONS_FILTER_OPTION.key)
                    : undefined}
                  color={filters.filter !== ALL_JOB_APPLICATIONS_FILTER_OPTION.key
                    ? 'neutral'
                    : 'primary'}
                />
              </HStack>
            )}

            {isMobile && (
              <Select
                value={filters.filter ?? defaultFilterKey}
                aria-label="Seleziona un filtro della candidatura (mobile)"
                options={FILTER_OPTIONS(filterContext).map(({ label, key }) => ({
                  label: label.long,
                  value: key,
                }))}
                size="S"
                onChange={({ target }) => handleBadgeClick(target.value as JobApplicationSerpPageFilterKey)()}
              />
            )}
          </div>
          <HStack
            alignItems="center"
            gap={100}
          >
            <SearchByQueryAction
              initialQuery={filters.q}
              onSearch={handleSearch}
              width="20rem"
              placeholder="Cerca per contatto o note"
              size={!isMobile ? 'M' : 'S'}
              variant={!isMobile ? 'input' : 'action'}
            />

            {/* In case of an an agent user, the list of agents includes the agent itself and its subordinates */}
            <FilterSerpJobApplicationsAction
              hasSubordinates={agents.length > 1}
              size={!isMobile ? 'M' : 'S'}
            />

            <ActionIconDropdown
              label="Ordina"
              aria-label="Pulsante per selezionare l'ordinamento delle candidature"
              placement="bottom-end"
              icon={{ path: !filters.sort || filters.sort !== DEFAULT_SORT_OPTION.key ? ICON_CUSTOM_SORT_PLUS : ICON_SORT }}
              size={!isMobile ? 'M' : 'S'}
              color={filters.sort && filters.sort !== DEFAULT_SORT_OPTION.key ? 'primary' : 'neutral'}
              emphasis="low"
              options={SORT_OPTIONS.map(({ key, label }) => ({
                key,
                label,
                iconRight: (!filters.sort && key === DEFAULT_SORT_OPTION.key) || filters.sort === key ? { path: ICON_CHECK } : undefined,
                color: key !== DEFAULT_SORT_OPTION.key && filters.sort === key ? 'primary' : 'neutral',
                onClick: () => addFilters({ sort: key }),
              }))}
            />

          </HStack>
        </HStack>
      )}
    >
      <Card
        paddingSize="M"
        aria-label="Titolo pagina"
      >
        <Card.Header
          icon={{ path: ICON_BRIEFCASE_OUTLINE }}
          title={`Candidature${filterLabel ? ` ${filterLabel}` : ''}${(serpJobApplications && serpJobApplications.totalElements > 0) ? ` (${serpJobApplications.totalElements})` : ''}`}
          primaryActions={[
            <CreateJobApplicationWithApplicantAction onSuccess={handleJobApplicationCreated}>
              {({ createJobApplication }) => (
                <Action
                  label="Aggiungi candidatura"
                  size="S"
                  onClick={createJobApplication}
                />
              )}
            </CreateJobApplicationWithApplicantAction>,
          ]}
        />
        <Card.Content>
          <SerpFiltersSummary
            aria-label="search-filters-summary"
            filters={{ ...filters, assigneeInterviewer: unionBy(filters.assigneeEmail, filters.interviewerEmail, (user) => user.toString()) }}
            configs={advancedFilterSummaryConfigs}
            sortOptions={SORT_OPTIONS}
          />
          <Spacing
            aria-label="Lista candidature"
            margin={[300, 0, 0]}
          >
            {!isMobile && areSerpJobApplicationsLoading && (
              <SimpleTableSkeleton aria-label="Candidature in caricamento (desktop)" />
            )}

            {isMobile && areSerpJobApplicationsLoading && (
              <Stack aria-label="Pagamenti in caricamento (mobile)">
                <CardSkeleton />
                <CardSkeleton />
                <CardSkeleton />
              </Stack>
            )}

            {serpJobApplicationsError && (
              <Message
                message={LOAD_SERP_JOB_APPLICATIONS_ERROR_MESSAGE}
                type="critical"
                boxed
              />
            )}

            {(serpJobApplications && serpJobApplications.totalElements === 0) && (
              <Message
                message={NO_SERP_JOB_APPLICATIONS_MESSAGE}
                boxed
              />
            )}

            {serpJobApplications && serpJobApplications.totalElements > 0 && (
              <PaginatedList
                {...serpJobApplications}
                hideResultCount
                goToPage={goToPage}
              >
                {!isMobile && (
                  <SimpleTable aria-label="Lista candidature (desktop)">
                    <SimpleTable.Body>
                      {serpJobApplications.content.map((serpJobApplication) => (
                        <SimpleTable.Row
                          asChild
                          key={serpJobApplication.jobApplication.id}
                        >
                          <ViewSerpJobApplicationTableRow
                            serpJobApplication={serpJobApplication}
                            onJobApplicationUpdate={mutateJobApplicationsWithDelay}
                            onRecallUpdate={mutateJobApplicationsWithDelay}
                            onInterviewUpdate={mutateJobApplicationsWithDelay}
                          />
                        </SimpleTable.Row>
                      ))}
                    </SimpleTable.Body>
                  </SimpleTable>
                )}

                {isMobile && serpJobApplications.content.map((serpJobApplication) => (
                  <ViewSerpJobApplicationCard
                    key={serpJobApplication.jobApplication.id}
                    serpJobApplication={serpJobApplication}
                    onJobApplicationUpdate={mutateJobApplicationsWithDelay}
                    onRecallUpdate={mutateJobApplicationsWithDelay}
                    onInterviewUpdate={mutateJobApplicationsWithDelay}
                  />
                ))}
              </PaginatedList>
            )}
          </Spacing>
        </Card.Content>
      </Card>
    </SimplePageLayout>
  );
};

export default JobApplicationSerpPage;
